import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import { TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { useMarketData } from '@widget/core';
import ThemeProvider from '@toolbox/style/theme/common/ThemeProvider';
import { filterZipCodes } from '@widget/utils/filterZipCodes';
import { isValidZipCode } from '@widget/utils/validateZipCode';
import {
  extractCityFromString,
  extractZipCodeFromString,
} from '@widget/utils/extractZipCityFromString';
import { getCountryName, getZipCodeLabel } from '@widget/utils/getZipCodeLabel';
import { ZipCodesQueryKey, ZipCodesQueryFn } from '@widget/api/queries';
import PopperBottom from '@toolbox/components/PopperBottom';

import type { SyntheticEvent } from 'react';
import type { ZipCodeType } from '@widget/api/queries/zipcodes/types';

export type ZipCodeFieldProps = {
  name?: string;
  label?: string | null;
  coverageFieldName?: string | null;
  keepErrorSpace?: boolean;
};

// @todo new error msg ?
const defaultErrorMessage = 'Ungültige Postleitzahl';

export const ZipCodeField = ({
  name = 'zipCode',
  coverageFieldName,
  ...props
}: ZipCodeFieldProps): JSX.Element => {
  const { market } = useMarketData();
  const { data, isFetching } = useQuery({
    queryKey: ZipCodesQueryKey.getByMarket(market),
    queryFn: ZipCodesQueryFn.getByMarket,
    staleTime: Infinity,
  });

  const { setValue, register, formState, trigger, getValues } = useFormContext();

  const zipCode = getValues()[name] || '';
  const city = getValues().city || '';
  const region = getValues().region || '';
  const [userInput, setUserInput] = useState(
    zipCode ? `${zipCode}, ${city || region}, ${getCountryName(market)}` : '',
  );

  useEffect(() => {
    if (coverageFieldName) {
      setValue(coverageFieldName, undefined);
    }
  }, [coverageFieldName]);

  const handleInputChange = (event: SyntheticEvent, value: string) => {
    if (event === null) return;
    const zip = extractZipCodeFromString(value);
    const city = extractCityFromString(value);
    setUserInput(value);
    setValue(name, zip);
    setValue('city', city || null);
    setValue('region', null);
  };

  const handleOnChange = (_: SyntheticEvent, value: NonNullable<ZipCodeType | string>) => {
    if (typeof value !== 'string' && value !== null) {
      setValue(name, value.zipCode);
      setValue('city', value.city);
      setValue('region', value.region);
    }
    trigger();
  };

  const filterOptions = filterZipCodes({ zipCode, city, market, isFetching });
  const getOptionLabel = getZipCodeLabel(market);
  const handleBlur = () => {
    if (isValidZipCode({ options: data, zipCode, city, region })) return;
    const firstOption = filterOptions(data || [])[0];
    if (!firstOption) {
      return;
    }

    setUserInput(getOptionLabel(firstOption));
    setValue(name, firstOption.zipCode);
    setValue('city', firstOption.city);
    setValue('region', firstOption.region);
    trigger();
  };

  return (
    <ThemeProvider>
      <Autocomplete
        fullWidth
        clearOnBlur={false}
        clearIcon={null}
        disableClearable={true}
        freeSolo
        value={userInput}
        onInputChange={handleInputChange}
        onChange={handleOnChange}
        options={data || []}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={'outlined'}
            error={formState.isDirty && formState.errors[name]}
            helperText={formState.isDirty && formState.errors[name]?.message}
            label={props.label}
            sx={{ marginBottom: 2 }}
            InputProps={{
              ...params.InputProps,
              endAdornment: null,
            }}
            {...register(name, {
              required: defaultErrorMessage,
              validate: (value) => {
                const city = getValues().city;
                const region = getValues().region;
                return isValidZipCode({ options: data, zipCode: value, city, region })
                  ? true
                  : defaultErrorMessage;
              },
            })}
          />
        )}
        noOptionsText={''}
        PopperComponent={(props) => <PopperBottom {...props} />}
        onBlur={handleBlur}
      />
    </ThemeProvider>
  );
};
